import { useCallback, useEffect, useRef } from 'react'
import { BanIcon, FilePlusIcon } from 'lucide-react'
import { useNavigate } from 'react-router'

import { Button } from '../ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from '../ui/empty'
import { ScrollArea } from '../ui/scroll-area'
import { Skeleton } from '../ui/skeleton'

import { paths } from '@/config/path'
import { ListService, useInfiniteServices } from '@/features/services/api/get-services'
import { ETA_UNITS_LABELS } from '@/features/services/components/service-eta-select'
import { CreateServiceModal, ModalRenderArgs } from '@/state/modals'

export function CreateServiceModalContent({ slots, ctx }: ModalRenderArgs<CreateServiceModal>) {
  const { Header, Body, Title, Description, Footer } = slots
  const navigate = useNavigate()

  const handleCreateNewService = (templateId?: string) => {
    ctx.closeAll()
    navigate(paths.app['create-service'].getHref(templateId))
  }

  return (
    <>
      <Header className="space-y-3">
        <Title>Créer un service</Title>
        <Description />
      </Header>
      <Body>
        <div className="grid gap-2">
          <div
            className="aspect-square w-28 h-28 border-border border rounded-md cursor-pointer flex flex-col items-center justify-center gap-2 hover:bg-muted transition-colors"
            onClick={() => handleCreateNewService()}
          >
            <FilePlusIcon className="size-6 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Nouveau service</span>
          </div>
        </div>
        <TemplatePickerList onTemplateSelect={handleCreateNewService} />
      </Body>
      <Footer>
        <Button variant="outline" onClick={ctx.closeAll}>
          Annuler
        </Button>
      </Footer>
    </>
  )
}

function TemplatePickerList({ onTemplateSelect }: { onTemplateSelect: (id: string) => void }) {
  const serviceTemplatesQuery = useInfiniteServices({ origin: 'predefined' })

  if (serviceTemplatesQuery.isPending) {
    return (
      <div>
        <span>Utiliser un modèle de service</span>
        <div className="flex flex-col gap-3 mt-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-32 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (!serviceTemplatesQuery.data || serviceTemplatesQuery.data.pages.length === 0) {
    return <span className="text-sm text-muted-foreground">Aucun modèle de service disponible</span>
  }

  const templates = serviceTemplatesQuery.data.pages.flatMap((page) => page.data)

  return (
    <ScrollArea className="max-h-96">
      <span className="text-foreground">Utiliser un modèle de service</span>
      <ServiceTemplateList
        templates={templates}
        onItemSelect={onTemplateSelect}
        onReachEnd={() => serviceTemplatesQuery.fetchNextPage()}
      />
    </ScrollArea>
  )
}

type ServiceTemplateListProps = {
  templates: ListService[]
  onItemSelect?: (id: string) => void
  onReachEnd?: () => void
}
function ServiceTemplateList({ templates, onItemSelect, onReachEnd }: ServiceTemplateListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement || !onReachEnd) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px threshold

      if (isNearBottom) {
        onReachEnd()
      }
    }

    scrollElement.addEventListener('scroll', handleScroll)
    return () => scrollElement.removeEventListener('scroll', handleScroll)
  }, [onReachEnd])

  return (
    <div ref={scrollRef} className="flex flex-col gap-3 mt-2">
      {templates.length === 0 && (
        <Empty className="from-muted/50 to-background h-full bg-gradient-to-b from-30%">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BanIcon />
            </EmptyMedia>
            <EmptyDescription>
              Aucun modèle de service disponible pour le moment. Veuillez créer un service personnalisé.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      {templates.map((template) => (
        <ServiceTemplateItem key={template.id} template={template} onSelect={onItemSelect} />
      ))}
    </div>
  )
}

type ServiceTemplateItemProps = {
  template: ListService
  onSelect?: (id: string) => void
}
function ServiceTemplateItem({ template, onSelect }: ServiceTemplateItemProps) {
  const handleSelect = useCallback(() => onSelect?.(template.id), [onSelect, template.id])

  return (
    <div
      key={template.id}
      className="min-w-52 w-full  min-h-32 max-h-32 p-2 border border-border rounded-lg flex flex-col bg-muted/40"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <span className="text-sm font-semibold capitalize truncate">{template.name}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {template.price} € / {template.eta}{' '}
          {ETA_UNITS_LABELS[template.eta_unit as keyof typeof ETA_UNITS_LABELS] || template.eta_unit}
        </span>
      </div>
      <p className="text-xs text-muted-foreground text-wrap truncate mt-2">{template.description}</p>
      <div className="flex gap-2 ml-auto mt-auto">
        <Button variant={'outline'} size={'sm'} onClick={handleSelect}>
          Utiliser
        </Button>
      </div>
    </div>
  )
}
